
import React, { useState, useCallback } from 'react';
import { useProfileAbility } from 'context/profile';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'components/Shared/Toast';
import { fetcher } from 'api/client';
import { fetchWorkflows, deleteWorkflow, createWorkflow } from 'api/actions/workflows';
import { noop, formatDistanceToNow } from 'utils'
import { useTranslation } from 'react-i18next';
import { Navbar, Button, Card, CardGroup, CardTitle, CardSubtitle, CardBody, Col, Container, Row, Media, CardText, Spinner } from 'reactstrap';
import clsx from 'clsx';

import bootbox from 'components/Modals/Bootbox';
import ItemDetail from 'components/Shared/ItemDetail';
import { WorkflowSteps } from './Contents'
import UpgradePlan from 'components/Shared/UpgradePlan';
import { MainHeader } from 'components/Shared/MainHeader';

const tabs = [
    {
        name: 'timeline',
        icon: 'code-branch',
        Component: WorkflowSteps
    }
];

function WorkflowItem({ overlay, workflow = {}, removeWorkflow = noop, openWorkflow = noop, openWorkflowId }) {

    const { t } = useTranslation();
    const handleRemove = async () => {
        if (await bootbox.confirm(t('workflows.confirm_remove'))) removeWorkflow(workflow._id);
    }
    const handleOpenWorkflow = useCallback(() => {
        openWorkflow(workflow);
    }, [workflow]);

    const profileAbility = useProfileAbility();

    return (
        <Col md="4" className="mt-3">
            <Card className={clsx('shadow-sm rounded-lg', openWorkflowId === workflow._id && 'shadow')} >
                <CardBody>
                    <Button size="sm" color="default" className={clsx('float-right pointer')} hidden={!profileAbility.can('remove', { ...workflow, __type: 'Workflow' })} onClick={handleRemove}>
                        <i className="fas fa-times-circle text-danger"></i>
                    </Button>
                    <div className="pointer" onClick={handleOpenWorkflow}>
                        <Media>
                            <img src="/images/workflow/workflow.png" height="30px" alt="" />
                            <CardTitle tag="h5" className="text-break ml-3 text-truncate">{workflow.name}</CardTitle>
                        </Media>
                        <CardSubtitle className="text-muted mt-3"> {t('workflow.creator')}: {workflow.creator?.givenName} {workflow.creator?.familyName} {formatDistanceToNow(workflow.createdAt)}</CardSubtitle>
                        <CardGroup className="text-center mt-3">
                            <Card className="border-0">
                                <h4 className="font-weight-bold">{workflow.contracts}</h4>
                                <small className="text-muted">{t('workflow.contract_number')}</small>
                            </Card>
                            <Card className="border-0">
                                <h4 className="font-weight-bold">{workflow.steps?.length}</h4>
                                <small className="text-muted">{t('workflow.stage_number')}</small>
                            </Card>
                        </CardGroup>
                    </div>
                </CardBody>
            </Card>
        </Col>
    )
}

function EmptyWorkflowItem() {
    return (
        [...Array(12)].map((x, index) =>
            <Col md="4" className="mt-3" key={`w${index}`}>
                <Card className={clsx('rounded-lg shadow-sm rounded bg-light-gray pulse-animation')} style={{ height: '166px' }}>
                </Card>
            </Col>
        )
    )
}


export default function Workflows() {
    const { t } = useTranslation();

    const profileAbility = useProfileAbility();

    const queryClient = useQueryClient();
    const onError = ({ message = 'GeneralError' } = {}) => {
        toast.error(t(`errors.${message}`))
    }

    const onSuccess = () => {
        toast.success(t('success'))
        queryClient.invalidateQueries('workflows-fetch');
    }

    const onSuccessCreation = () => {
        toast.success(t('workflows.creation_success'))
        queryClient.invalidateQueries('workflows-fetch');
    }

    const { data: workflows, isSuccess, isLoading } = useQuery('workflows-fetch', () => fetcher(fetchWorkflows()));

    const [currentWorkflow, setCurrentworkflow] = useState({});

    const { mutate: mutateDeleteWorkflow } = useMutation((workflowId) =>
        fetcher(deleteWorkflow(workflowId),), { onSuccess, onError });

    const { mutate: mutateCreateWorkflow } = useMutation((workflow) =>
        fetcher(createWorkflow(workflow),), { onSuccess: onSuccessCreation, onError });

    const removeWorkflow = (workflowId) => mutateDeleteWorkflow(workflowId);

    const hasCommittedChanges = async () => {
        if (currentWorkflow.hasChanges) {
            return bootbox.confirm(t('workflows.confirm_close'))
        } return true;
    }

    const openWorkflow = async (workflow) => {
        if (await hasCommittedChanges()) {
            setCurrentworkflow({ ...workflow });
        }
    }

    const handleCreateWorkflow = () => mutateCreateWorkflow({ name: t('workflows.new.title'), steps: [{ type: 'initial', order: 0 }] })

    const closeCurrentWorkflow = async () => {
        if (await hasCommittedChanges()) {
            setCurrentworkflow({});
        }
    }

    if (!profileAbility.can('VIEW_WORKFLOW')) return <UpgradePlan />;

    return (
        <>
            <MainHeader title="workflow.title">
                <Button color="default" onClick={handleCreateWorkflow} title={t('workflow.new.new')}>
                    <i className="fas fa-plus fa-2x text-primary"></i>
                </Button>
            </MainHeader>
            <Container fluid>
                <Row>
                    {
                        isLoading &&
                        <EmptyWorkflowItem />
                    }
                    {isSuccess && workflows?.map((workflow) => (
                        <WorkflowItem
                            openWorkflow={openWorkflow}
                            removeWorkflow={removeWorkflow}
                            openWorkflowId={currentWorkflow._id}
                            workflow={workflow}
                            key={workflow._id}
                        />
                    ))}
                </Row>
                <ItemDetail
                    isOpen={currentWorkflow._id}
                    title={currentWorkflow.name}
                    onClose={closeCurrentWorkflow}
                    tabs={tabs}
                    workflowId={currentWorkflow._id}
                    setCurrentworkflow={setCurrentworkflow}
                    currentWorkflow={currentWorkflow}
                />
            </Container>
        </>
    )
}